// Libraries
import { Children, FC, ReactNode, isValidElement } from "react";
import { Root, Overlay, Content, Portal } from "@radix-ui/react-dialog";

// Types
import { ModalProps } from "./Modal.types";
import { ModalHeader } from "./ModalHeader/ModalHeader";
import { ModalSubtitle } from "./ModalSubtitle/ModalSubtitle";
import { cn } from "utils/cn";
import { preventDialogDismissOnPortaledOverlay } from "./modalPortaledOverlayDismiss";

const positionMap = {
  center: "items-center justify-center",
  top: "items-start justify-center pt-4",
  bottom: "items-end justify-center pb-4",
};

const hasRenderableContent = (value: ReactNode): boolean => {
  if (value === null || value === undefined || typeof value === "boolean") {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.some(hasRenderableContent);
  }

  return true;
};

const hasModalDescription = (children: ReactNode): boolean => {
  return Children.toArray(children).some((child) => {
    if (!isValidElement<{ children?: ReactNode; subtitle?: ReactNode }>(child)) {
      return false;
    }

    if (child.type === ModalSubtitle && hasRenderableContent(child.props.children)) {
      return true;
    }

    if (child.type === ModalHeader && hasRenderableContent(child.props.subtitle)) {
      return true;
    }

    return hasModalDescription(child.props.children);
  });
};

export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  layout = "default",
  position = "center",
  maxWidth = "max-w-lg",
  className = "",
  backdropClassName = "",
  dialogPanelStyle,
  ariaDescribedBy,
  getReturnFocusTarget,
}) => {
  const isPanel = layout === "panel";
  const contentDescriptionProps = ariaDescribedBy
    ? { "aria-describedby": ariaDescribedBy }
    : hasModalDescription(children)
      ? {}
      : { "aria-describedby": undefined };

  return (
    <Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
      modal
    >
      <Portal>
        <Overlay
          className={`fixed inset-0 z-[250] bg-card/75 ${backdropClassName}`}
          aria-hidden="true"
          data-testid="modal-backdrop"
        />
        <div
          className={`fixed inset-0 z-[250] flex pointer-events-none ${positionMap[position]}`}
          // Radix locks body scroll on open and hides the scrollbar, which widens the
          // viewport. This fixed wrapper ignores the body's compensation padding, so without
          // this the centered dialog would jump sideways. Reserve the removed scrollbar width
          // (set by react-remove-scroll) to keep the dialog perfectly still on open.
          style={{ paddingRight: "var(--removed-body-scroll-bar-size, 0px)" }}
        >
          <Content
            {...contentDescriptionProps}
            onPointerDownOutside={preventDialogDismissOnPortaledOverlay}
            onInteractOutside={preventDialogDismissOnPortaledOverlay}
            onFocusOutside={preventDialogDismissOnPortaledOverlay}
            onCloseAutoFocus={
              getReturnFocusTarget
                ? (event) => {
                    const target = getReturnFocusTarget();
                    if (!target?.isConnected) return;
                    event.preventDefault();
                    target.focus();
                  }
                : undefined
            }
            className={cn(
              "pointer-events-auto w-full shadow-[0_4px_20px_rgba(17,18,18,0.9)]",
              isPanel
                ? "overflow-hidden rounded-lg border border-border bg-card p-0"
                : "rounded-2xl bg-card p-8",
              maxWidth,
              className
            )}
            style={dialogPanelStyle}
            data-testid="modal-panel"
          >
            <div className={cn("flex flex-col", isPanel ? "gap-0" : "gap-4")}>{children}</div>
          </Content>
        </div>
      </Portal>
    </Root>
  );
};

export default Modal;
